import { useState, useEffect, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Loader2,
  Activity,
  History,
  MoreHorizontal,
  Pencil,
  Plus,
  StopCircle,
  Undo2,
} from "lucide-react";
import { EmptyState } from "@/components/consultation/EmptyState";
import {
  useAllMonitoringPlanChanges,
  useMonitoringPlan,
} from "@/hooks/useMonitoringPlan";
import { useMonitoringFrequencies } from "@/hooks/useFrequencies";
import { useDirtyFlag } from "@/hooks/useUnsavedChanges";
import { appointmentsAPI } from "@/lib/appointments-api";
import { cn } from "@/lib/utils";
import { AddMonitoringAdviceDialog } from "./AddMonitoringAdviceDialog";
import { MonitoringAdviceHistoryDialog } from "./MonitoringAdviceHistoryDialog";
import { format, parseISO } from "date-fns";
import { formatActor, formatAppointmentRef } from "./planFormat";
import {
  adviceChanged,
  computeNetChanges,
  frequencyChanged,
  startStateByTracker,
  type NetChange,
} from "./planDiff";
import type { MonitoringPlanEntry } from "@/types/monitoring";
import type { ParentType } from "@/lib/monitoring-api";

interface MonitoringAdviceCardProps {
  appointmentId: string;
  isCompleted?: boolean;
  // Kept so existing mounts keep working; the plan is per patient, not per
  // parent type.
  parentType?: ParentType;
}

type SavePayload = {
  tracker: number;
  frequency: number | null;
  advice: string;
};

type RowKind = "ongoing" | "new" | "changed" | "stopped";

const ROW_GRID = "grid grid-cols-[4fr_3fr_4fr_auto] items-start gap-3";

// Each part of the plan sits in its own card so "what the patient is already
// on" and "what changed today" never blur together. The ongoing plan is a
// quiet inset; this visit carries the primary accent because it is where the
// clinician acts.
const PlanSection = ({
  title,
  subtitle,
  tone,
  children,
}: {
  title: string;
  subtitle?: string;
  tone: "settled" | "active";
  children: ReactNode;
}) => (
  <section
    className={cn(
      "rounded-lg p-2 border",
      tone === "settled"
        ? "bg-muted/40 border-border/60"
        : "bg-card border-primary/30"
    )}
  >
    <div className="flex items-baseline justify-between gap-3 px-3 pt-1 pb-2 border-b border-border/60">
      <span
        className={cn(
          "z-eyebrow",
          tone === "active" ? "text-primary" : "text-muted-foreground"
        )}
      >
        {title}
      </span>
      {subtitle && (
        <span className="z-hint text-muted-foreground">{subtitle}</span>
      )}
    </div>
    <div className="pt-1">{children}</div>
  </section>
);

const KIND_BADGE: Record<
  Exclude<RowKind, "ongoing">,
  { variant: "success" | "warning" | "critical"; label: string }
> = {
  new: { variant: "success", label: "New" },
  changed: { variant: "warning", label: "Changed" },
  stopped: { variant: "critical", label: "Stopped" },
};

// Light-mode --success is near-black forest; in this card "New" borrows the
// palette's mid-forest chart hue so it reads green. Dark mode keeps --success.
// Local to this card on purpose: the shared token is unchanged.
const NEW_GREEN_BADGE =
  "bg-[oklch(var(--chart-3)/0.15)] text-[oklch(var(--chart-3))] dark:bg-success/20 dark:text-success";

// A left rule in the change's colour, so the kind reads before the badge.
const KIND_RULE: Record<Exclude<RowKind, "ongoing">, string> = {
  new: "border-l-2 border-l-[oklch(var(--chart-3))] dark:border-l-success",
  changed: "border-l-2 border-l-warning",
  stopped: "border-l-2 border-l-destructive",
};

// Frequency select + advice textarea; saves on change / blur (as before).
const EditableFields = ({
  entry,
  onSave,
}: {
  entry: MonitoringPlanEntry;
  onSave: (data: SavePayload) => void;
}) => {
  const { data: frequencies } = useMonitoringFrequencies();
  const [advice, setAdvice] = useState(entry.advice || "");
  const needsFrequencySelection = entry.frequency == null;

  useEffect(() => {
    setAdvice(entry.advice || "");
  }, [entry.advice, entry.id]);

  const handleSaveAdvice = () => {
    if (advice !== (entry.advice || "")) {
      onSave({
        tracker: entry.tracker.id,
        frequency: entry.frequency?.id ?? null,
        advice,
      });
    }
  };

  const handleFrequencyChange = (val: string) => {
    const parsed = val ? parseInt(val) : null;
    if (parsed === null || Number.isNaN(parsed)) return;
    if (parsed === entry.frequency?.id) return;
    onSave({
      tracker: entry.tracker.id,
      frequency: parsed,
      advice: entry.advice || "",
    });
  };

  return (
    <>
      <Select
        value={
          needsFrequencySelection ? undefined : String(entry.frequency?.id)
        }
        onValueChange={handleFrequencyChange}
      >
        <SelectTrigger
          className={cn(
            "z-secondary",
            needsFrequencySelection &&
              "border-warning/60 ring-1 ring-warning/30"
          )}
        >
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {frequencies?.map((f) => (
            <SelectItem key={f.id} value={f.id.toString()}>
              {f.display_text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Textarea
        rows={1}
        placeholder="Specific advice"
        value={advice}
        onChange={(e) => setAdvice(e.target.value)}
        onBlur={handleSaveAdvice}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSaveAdvice();
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
        className="min-h-9 py-2 z-secondary"
      />
    </>
  );
};

const PlanRow = ({
  kind,
  entry,
  before,
  since,
  readOnly,
  editing,
  busy,
  onSave,
  onChange,
  onDoneEditing,
  onRequestStop,
  onUndo,
}: {
  kind: RowKind;
  /** Entry to show: the current one, or the pre-visit one for stopped rows. */
  entry: MonitoringPlanEntry;
  /** Pre-visit state, for "changed" rows. */
  before?: MonitoringPlanEntry | null;
  /** Entry whose start the "since" line shows (ongoing rows); defaults to `entry`. */
  since?: MonitoringPlanEntry | null;
  readOnly: boolean;
  editing?: boolean;
  busy?: boolean;
  onSave: (data: SavePayload) => void;
  onChange?: () => void;
  onDoneEditing?: () => void;
  onRequestStop?: () => void;
  onUndo?: () => void;
}) => {
  const stopped = kind === "stopped";
  const showFields = !readOnly && !stopped && (kind !== "ongoing" || editing);
  const prev = kind === "changed" ? before : null;
  const origin = since ?? entry;
  const freqWas = prev && frequencyChanged(prev, entry);
  const adviceWas = prev && adviceChanged(prev, entry);

  return (
    <div
      className={cn(
        ROW_GRID,
        "px-3 py-2.5 border-b border-border/60 last:border-b-0 transition-colors",
        kind === "ongoing" ? "rounded-md hover:bg-muted/30" : KIND_RULE[kind]
      )}
    >
      <div className="self-center min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              "z-secondary font-medium truncate",
              stopped ? "text-muted-foreground line-through" : "text-foreground"
            )}
          >
            {entry.tracker.name}
          </span>
          {kind !== "ongoing" && (
            <Badge
              variant={KIND_BADGE[kind].variant}
              className={kind === "new" ? NEW_GREEN_BADGE : undefined}
            >
              {KIND_BADGE[kind].label}
            </Badge>
          )}
        </div>
        {prev && (freqWas || adviceWas) && (
          <div className="z-hint text-muted-foreground mt-0.5 space-y-0.5">
            {freqWas && (
              <p>
                Was {prev.frequency?.name ?? "no frequency"} →{" "}
                <span className="text-foreground">
                  {entry.frequency?.name ?? "no frequency"}
                </span>
              </p>
            )}
            {adviceWas && (
              <p className="truncate">
                Advice was {prev.advice ? `“${prev.advice}”` : "empty"}
              </p>
            )}
          </div>
        )}
        {kind === "ongoing" && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="z-hint text-muted-foreground cursor-default">
                since {format(parseISO(origin.started_at), "d MMM")}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Added by {formatActor(origin.started_by)} ·{" "}
              {formatAppointmentRef(origin.started_appointment)}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {showFields ? (
        <EditableFields entry={entry} onSave={onSave} />
      ) : (
        <>
          <div
            className={cn(
              "z-secondary self-center",
              stopped
                ? "text-muted-foreground line-through"
                : "text-foreground",
              !stopped && entry.frequency == null && "text-warning"
            )}
          >
            {entry.frequency?.name ?? "Frequency not set"}
          </div>
          <div
            className={cn(
              "z-secondary self-center text-muted-foreground",
              stopped && "line-through"
            )}
          >
            {entry.advice || "—"}
          </div>
        </>
      )}

      <div className="flex items-center justify-center gap-1 self-center">
        {!readOnly && kind === "ongoing" && !editing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Row actions"
                disabled={busy}
              >
                {busy ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <MoreHorizontal className="size-3.5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onChange}>
                <Pencil /> Change
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onSelect={onRequestStop}>
                <StopCircle /> Stop
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!readOnly && kind === "ongoing" && editing && (
          <Button variant="ghost" size="sm" onClick={onDoneEditing}>
            Done
          </Button>
        )}
        {!readOnly && kind !== "ongoing" && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Undo"
            title="Undo"
            onClick={onUndo}
            disabled={busy}
          >
            {busy ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Undo2 className="size-3.5" />
            )}
          </Button>
        )}
      </div>

    </div>
  );
};

export const MonitoringAdviceCard = ({
  appointmentId,
  isCompleted = false,
  parentType = "prescriptions",
}: MonitoringAdviceCardProps) => {
  // Same query/key the consultation page uses, so this is a cache hit.
  const { data: appointmentDetails } = useQuery({
    queryKey: ["appointmentDetails", appointmentId],
    queryFn: () => appointmentsAPI.getAppointmentDetails(appointmentId),
    enabled: !!appointmentId,
  });
  const patientId = appointmentDetails?.patient_id || "";
  const readOnly =
    isCompleted || appointmentDetails?.appointment_status === "completed";
  const visitRef = appointmentDetails?.zarity_appointment_id;

  // A completed appointment shows the plan as it was when the visit was
  // completed; the server resolves that time from the appointment.
  const {
    data: plan,
    isLoading,
    upsertEntry,
    stopEntry,
  } = useMonitoringPlan(patientId, readOnly ? appointmentId : undefined);
  const { changes, isLoading: changesLoading } = useAllMonitoringPlanChanges(
    patientId,
    !!patientId
  );

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [stopEntryId, setStopEntryId] = useState<string | null>(null);
  const [editingTracker, setEditingTracker] = useState<number | null>(null);
  const [busyTracker, setBusyTracker] = useState<number | null>(null);

  const entries = useMemo(() => plan ?? [], [plan]);

  // This visit's net changes: earliest `before` vs the live plan (planDiff.ts).
  const visitRecords = useMemo(
    () => changes.filter((c) => c.appointment?.id === appointmentId),
    [changes, appointmentId]
  );
  const netChanges = useMemo(() => {
    const byTracker = new Map(entries.map((e) => [e.tracker.id, e]));
    return computeNetChanges(visitRecords, (id) => byTracker.get(id) ?? null);
  }, [visitRecords, entries]);
  // For trackers this visit touched but left as they were, keep the original
  // start (an undo creates a fresh entry that would otherwise read "since today").
  const visitStart = useMemo(
    () => startStateByTracker(visitRecords),
    [visitRecords]
  );

  const changedTrackerIds = useMemo(
    () => new Set(netChanges.map((n) => n.trackerId)),
    [netChanges]
  );
  const ongoing = useMemo(
    () => entries.filter((e) => !changedTrackerIds.has(e.tracker.id)),
    [entries, changedTrackerIds]
  );

  const dirtyKey = useMemo(
    () => `monitoring-advice-frequency:${parentType}:${appointmentId}`,
    [parentType, appointmentId]
  );
  useDirtyFlag(
    dirtyKey,
    entries.some((e) => e.frequency == null),
    "Monitoring Advice — Frequency"
  );

  const entryPendingStop = entries.find((e) => e.id === stopEntryId) || null;

  const handleSave = (data: SavePayload) => {
    setEditingTracker(null);
    upsertEntry.mutate(
      {
        tracker: data.tracker,
        ...(data.frequency != null ? { frequency: data.frequency } : {}),
        advice: data.advice,
        appointment_id: appointmentId,
      },
      {
        onError: () =>
          toast.error("Couldn't save the change. Check the list and retry."),
      }
    );
  };

  const handleConfirmStop = () => {
    if (!stopEntryId) return;
    stopEntry.mutate(
      { entryId: stopEntryId, data: { appointment_id: appointmentId } },
      {
        onError: () =>
          toast.error("Couldn't remove it. Check the list and retry."),
      }
    );
    setStopEntryId(null);
  };

  const handleUndo = (net: NetChange) => {
    const onError = () =>
      toast.error("Couldn't undo the change. Check the list and retry.");
    const onSettled = () => setBusyTracker(null);
    setBusyTracker(net.trackerId);
    if (net.kind === "new" && net.after) {
      stopEntry.mutate(
        { entryId: net.after.id, data: { appointment_id: appointmentId } },
        { onError, onSettled }
      );
    } else if (net.before) {
      upsertEntry.mutate(
        {
          tracker: net.trackerId,
          ...(net.before.frequency
            ? { frequency: net.before.frequency.id }
            : {}),
          advice: net.before.advice ?? "",
          appointment_id: appointmentId,
        },
        { onError, onSettled }
      );
    }
  };

  const changeCount = netChanges.length;
  const showLoading =
    isLoading || changesLoading || (!patientId && !appointmentDetails);
  const isEmpty = entries.length === 0 && changeCount === 0;

  return (
    <>
      <Widget>
        <WidgetHeader
          title="Monitoring Advice"
          hasInfo
          extras={
            <button
              type="button"
              className="flex items-center justify-center size-5 rounded-md bg-muted text-muted-foreground hover:bg-muted/70 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setIsHistoryDialogOpen(true);
              }}
              title="View History"
            >
              <History className="size-3" />
            </button>
          }
        />
        <WidgetContent>
          {showLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="size-5 animate-spin mr-2" />
              <span className="z-body">Loading advice…</span>
            </div>
          ) : (
            <div className="space-y-3">
              {ongoing.length > 0 && (
                <PlanSection
                  title="Ongoing plan"
                  subtitle="Started at earlier visits"
                  tone="settled"
                >
                  {ongoing.map((entry) => (
                    <PlanRow
                      key={entry.id}
                      kind="ongoing"
                      entry={entry}
                      since={visitStart.get(entry.tracker.id)}
                      readOnly={readOnly}
                      editing={editingTracker === entry.tracker.id}
                      busy={stopEntry.isPending && stopEntryId === entry.id}
                      onSave={handleSave}
                      onChange={() => setEditingTracker(entry.tracker.id)}
                      onDoneEditing={() => setEditingTracker(null)}
                      onRequestStop={() => setStopEntryId(entry.id)}
                    />
                  ))}
                </PlanSection>
              )}

              {(!readOnly || changeCount > 0 || isEmpty) && (
                <PlanSection
                  title={`This visit${visitRef ? ` · ${visitRef}` : ""}`}
                  tone="active"
                  subtitle={
                    changeCount > 0
                      ? `${changeCount} ${changeCount === 1 ? "change" : "changes"}`
                      : undefined
                  }
                >
                  {isEmpty ? (
                    <EmptyState
                      icon={<Activity />}
                      title="No monitoring tasks assigned"
                      variant="compact"
                    />
                  ) : changeCount === 0 ? (
                    <p className="z-secondary text-muted-foreground px-3 py-2">
                      No changes yet this visit
                    </p>
                  ) : (
                    netChanges.map((net) => {
                      const entry =
                        net.kind === "stopped" ? net.before : net.after;
                      if (!entry) return null;
                      return (
                        <PlanRow
                          key={net.trackerId}
                          kind={net.kind}
                          entry={entry}
                          before={net.before}
                          readOnly={readOnly}
                          busy={busyTracker === net.trackerId}
                          onSave={handleSave}
                          onUndo={() => handleUndo(net)}
                        />
                      );
                    })
                  )}
                  {!readOnly && (
                    <Button
                      className="w-full mt-2"
                      onClick={() => setIsAddDialogOpen(true)}
                      disabled={!patientId}
                    >
                      <Plus /> Add new
                    </Button>
                  )}
                </PlanSection>
              )}
            </div>
          )}
        </WidgetContent>
      </Widget>

      <AddMonitoringAdviceDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        appointmentId={appointmentId}
        patientId={patientId}
      />

      <MonitoringAdviceHistoryDialog
        open={isHistoryDialogOpen}
        onOpenChange={setIsHistoryDialogOpen}
        patientId={patientId}
      />

      <AlertDialog
        open={stopEntryId !== null}
        onOpenChange={(open) => !open && setStopEntryId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop monitoring?</AlertDialogTitle>
            <AlertDialogDescription>
              {entryPendingStop
                ? `“${entryPendingStop.tracker.name}” will be removed from this patient's monitoring plan from now on. Past visits keep it.`
                : "This will remove the selected tracker from the patient's monitoring plan."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleConfirmStop}
            >
              Stop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
